// src/pages/qc/PageRdPipeline.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { fmtDate } from "../../utils/dateUtils";
import { PipelineCreateModal } from "./PipelineCreateModal";
import { PipelineDetailModal } from "./PipelineDetailModal";
import { useToast } from "../../components/Toast";

export const STAGE_STYLE = {
  CONCEPT:     { bg: "#f3f4f6", color: "#374151",  label: "Concept" },
  LAB_TRIAL:   { bg: "#ede9fe", color: "#5b21b6",  label: "Lab Trial" },
  SCALE_UP:    { bg: "#dbeafe", color: "#1e40af",  label: "Scale-up" },
  EVALUATION:  { bg: "#fef3c7", color: "#92400e",  label: "Evaluation" },
  APPROVED:    { bg: "#d1fae5", color: "#065f46",  label: "Approved" },
  LAUNCHED:    { bg: "#dcfce7", color: "#166534",  label: "Launched" },
  CANCELLED:   { bg: "#fee2e2", color: "#991b1b",  label: "Cancelled" },
};

export const STATUS_STYLE = {
  ACTIVE:     { bg: "#d1fae5", color: "#065f46", label: "Active" },
  ON_HOLD:    { bg: "#fef3c7", color: "#92400e", label: "On Hold" },
  COMPLETED:  { bg: "#dbeafe", color: "#1e40af", label: "Completed" },
  CANCELLED:  { bg: "#fee2e2", color: "#991b1b", label: "Cancelled" },
};

export const PRIORITY_STYLE = {
  HIGH:   { color: "#dc2626", label: "High" },
  MEDIUM: { color: "#d97706", label: "Medium" },
  LOW:    { color: "#6b7280", label: "Low" },
};

export const PRODUCT_TYPES = [
  "Water Based",
  "Hot Melt",
  "Coolant",
  "Brake Fluid",
  "Solvent Based",
  "Other",
];

export const STAGES = [
  { value: "CONCEPT",    label: "Concept" },
  { value: "LAB_TRIAL",  label: "Lab Trial" },
  { value: "SCALE_UP",   label: "Scale-up" },
  { value: "EVALUATION", label: "Evaluation" },
  { value: "APPROVED",   label: "Approved" },
  { value: "LAUNCHED",   label: "Launched" },
  { value: "CANCELLED",  label: "Cancelled" },
];

export const STATUSES = [
  { value: "ACTIVE",    label: "Active" },
  { value: "ON_HOLD",   label: "On Hold" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

function StageBadge({ stage }) {
  const s = STAGE_STYLE[stage] ?? { bg: "#f3f4f6", color: "#374151", label: stage };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? { bg: "#f3f4f6", color: "#374151", label: status };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

export function PageRdPipeline({ myPerms }) {
  const showToast = useToast();
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [search,      setSearch]      = useState("");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [statusFilter,setStatusFilter]= useState("ACTIVE");
  const [createModal, setCreateModal] = useState(false);
  const [detailRow,   setDetailRow]   = useState(null);

  const canEdit   = myPerms?.includes("QC_EDIT") || myPerms?.includes("RND_EDIT");
  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    api.fetchPipeline()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      const matchStage  = stageFilter  === "ALL" || r.stage  === stageFilter;
      const matchStatus = statusFilter === "ALL" || r.status === statusFilter;
      const matchQ = !q ||
        (r.projectCode  || "").toLowerCase().includes(q) ||
        (r.name         || "").toLowerCase().includes(q) ||
        (r.assignedTo   || "").toLowerCase().includes(q) ||
        (r.productType  || "").toLowerCase().includes(q);
      return matchStage && matchStatus && matchQ;
    });
  }, [rows, search, stageFilter, statusFilter]);

  async function handleCreate(body) {
    const created = await api.createPipelineProject(body);
    setRows(old => [created, ...old]);
    setCreateModal(false);
    showToast("Project created");
  }

  async function handleUpdate(id, body) {
    const updated = await api.updatePipelineProject(id, body);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("Project updated");
    return updated;
  }

  async function handleDelete(id) {
    if (!window.confirm("Archive this project?")) return;
    await api.deletePipelineProject(id);
    setRows(old => old.filter(r => r.id !== id));
    setDetailRow(null);
    showToast("Project removed", "info");
  }

  const stageCounts = useMemo(() =>
    Object.fromEntries(STAGES.map(s => [s.value, rows.filter(r => r.stage === s.value && r.status === "ACTIVE").length])),
    [rows]
  );

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">R&amp;D Pipeline</div>
          <div className="page-subtitle">Research &amp; Development · Active projects and stage tracking</div>
        </div>
        {canEdit && (
          <div className="page-header-right">
            <button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ New Project</button>
          </div>
        )}
      </div>

      {/* Stage summary strip */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {STAGES.filter(s => s.value !== "CANCELLED").map(s => {
          const style  = STAGE_STYLE[s.value];
          const count  = stageCounts[s.value] || 0;
          const active = stageFilter === s.value;
          return (
            <button key={s.value}
              onClick={() => setStageFilter(active ? "ALL" : s.value)}
              style={{ background: active ? style.color : style.bg,
                color: active ? "#fff" : style.color,
                border: `1px solid ${style.color}`,
                borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600,
                cursor: "pointer" }}>
              {s.label} {count > 0 && <span style={{ opacity: 0.8 }}>· {count}</span>}
            </button>
          );
        })}
      </div>

      <div className="action-bar" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input className="form-input" placeholder="Search code, name, type, assigned to…"
          value={search} onChange={e => setSearch(e.target.value)} style={{ width: 280 }} />
        <select className="form-select" value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)} style={{ width: 160 }}>
          <option value="ALL">All Statuses</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
          {filtered.length} of {rows.length} projects
        </span>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Project Name</th>
                <th>Product Type</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Assigned To</th>
                <th>Target Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8}
                  style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                  No projects found
                </td></tr>
              ) : filtered.map(row => {
                const pri = PRIORITY_STYLE[row.priority] ?? PRIORITY_STYLE.MEDIUM;
                const overdue = row.targetDate && row.status === "ACTIVE"
                  && new Date(row.targetDate) < new Date();
                return (
                  <tr key={row.id} style={{ cursor: "pointer" }}
                    onClick={() => setDetailRow(row)}>
                    <td style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>
                      {row.projectCode}
                    </td>
                    <td className="fw-600">{row.name}</td>
                    <td style={{ fontSize: 12 }}>{row.productType || "—"}</td>
                    <td><StageBadge stage={row.stage} /></td>
                    <td><StatusBadge status={row.status} /></td>
                    <td style={{ fontSize: 12, fontWeight: 600, color: pri.color }}>
                      {pri.label}
                    </td>
                    <td style={{ fontSize: 12 }}>{row.assignedTo || "—"}</td>
                    <td style={{ fontSize: 12, color: overdue ? "#dc2626" : undefined }}>
                      {row.targetDate ? fmtDate(row.targetDate) : "—"}
                      {overdue && <span style={{ fontSize: 10, marginLeft: 4 }}>Overdue</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {createModal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setCreateModal(false); }}>
          <PipelineCreateModal onSave={handleCreate} onCancel={() => setCreateModal(false)} />
        </div>
      )}

      {detailRow && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setDetailRow(null); }}>
          <PipelineDetailModal
            project={detailRow}
            canEdit={canEdit}
            canDelete={canDelete}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onClose={() => setDetailRow(null)}
          />
        </div>
      )}
    </>
  );
}
